import axios from 'axios';

const API_ORIGIN = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_ORIGIN,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_ORIGIN}/api/v1/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          localStorage.setItem('access_token', response.data.access);
          api.defaults.headers.common.Authorization = `Bearer ${response.data.access}`;
          return axios.request(error.config);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'super_admin' | 'admin' | 'content_manager';
  phone?: string;
  profile_image?: string | null;
  is_active: boolean;
  created_at: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface TokenRefreshRequest {
  refresh: string;
}

export interface TokenRefreshResponse {
  access: string;
  refresh: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

export interface ResetPasswordRequest {
  user_id: string;
  new_password: string;
  new_password_confirm: string;
}

export interface UserCreateRequest {
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: 'super_admin' | 'admin' | 'content_manager';
  phone?: string;
  password: string;
  password_confirm: string;
}

export interface UserProfile {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  profile_image?: string | null;
  role: 'super_admin' | 'admin' | 'content_manager';
  is_active: boolean;
  created_at: string;
}

export interface UserProfileUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  profile_image?: File | null;
}

export interface Statistics {
  total_services: number;
  total_products: number;
  total_blog_posts: number;
  pending_requests: number;
  completed_requests: number;
  total_testimonials: number;
}

export interface Service {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  detailed_description: string;
  featured_image: string;
  featured_image_alt?: string;
  price_range: string;
  duration: string;
  is_featured: boolean;
  is_active: boolean;
  category: string;
  category_name: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  canonical_url?: string;
  robots_meta?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string | null;
  schema_markup?: any;
  focus_keyword?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string | null;
  is_active: boolean;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  posts_count?: number;
}

export interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  subtitle1: string;
  description: string;
  background_image1: string;
  background_image2: string | null;
  background_image3: string | null;
  feature_text1: string;
  feature_text2: string;
  feature_text3: string;
  order: number;
  is_active: boolean;
}

export interface Banner {
  id: string;
  banner_type: 'banner1' | 'banner2' | 'promotional' | 'cta';
  title: string;
  subtitle: string;
  description: string;
  background_image: string | null;
  background_color: string;
  cta_text: string;
  cta_link: string;
  order: number;
  is_active: boolean;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  brand: string;
  model_number?: string;
  description: string;
  short_description: string;
  price: string;
  featured_image: string;
  featured_image_alt?: string;
  gallery_images?: any;
  specifications?: any;
  is_featured: boolean;
  is_active: boolean;
  meta_title?: string;
  meta_description?: string;
  canonical_url?: string;
  robots_meta?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string | null;
  schema_markup?: any;
  created_at?: string;
  updated_at?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  featured_image: string;
  featured_image_alt?: string;
  is_featured: boolean;
  published_at: string | null;
  category: string;
  category_name: string;
  author_name: string;
  status: 'draft' | 'published' | 'archived';
  views_count: number;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  canonical_url?: string;
  robots_meta?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string | null;
  focus_keyword?: string;
  breadcrumbs?: string;
  schema_markup?: any;
  created_at?: string;
  updated_at?: string;
}

export interface AboutUs {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  mission: string;
  vision: string;
  featured_image: string | null;
  years_experience: number;
  happy_customers: number;
  projects_completed: number;
  team_members: number;
  is_active: boolean;
}

export interface Testimonial {
  id: string;
  customer_name: string;
  customer_image: string;
  rating: number;
  review: string;
  service_name: string;
  is_featured: boolean;
  is_active: boolean;
}

export type ContactRequestStatus = 'new' | 'in_progress' | 'resolved' | 'closed';

export interface ContactRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  request_type: 'general' | 'service' | 'quote' | 'support';
  status: ContactRequestStatus;
  service_title?: string | null;
  assigned_to_name?: string | null;
  created_at: string;
}

export type ServiceRequestStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
export type ServiceRequestPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface ServiceRequest {
  id: string;
  service_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  preferred_date: string;
  problem_description: string;
  priority: ServiceRequestPriority;
  status: ServiceRequestStatus;
  address: string;
  notes?: string;
  created_at: string;
}

export interface GalleryItem {
  id: string;
  title: string;
  description: string;
  image: string;
  image_alt: string;
  gallery_type: 'project' | 'service' | 'team' | 'office';
  order: number;
  is_active: boolean;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  faq_type: 'general' | 'service' | 'product';
  category?: string;
  service_title?: string | null;
  product_name?: string | null;
  order: number;
  is_active: boolean;
}

export interface WhyChooseUsBenefit {
  id: string;
  text: string;
  order: number;
  is_active: boolean;
  why_choose_us: string;
}

export interface WhyChooseUsFeature {
  id: string;
  title: string;
  description: string;
  icon_class: string;
  icon_color: string;
  order: number;
  is_active: boolean;
  why_choose_us: string;
}

export interface WhyChooseUsStat {
  id: string;
  label: string;
  value: string;
  icon_class: string;
  order: number;
  is_active: boolean;
  why_choose_us: string;
}

export interface WhyChooseUs {
  id: string;
  section_name: string;
  layout_type: 'features_with_stats' | 'feature_grid' | 'custom';
  title: string;
  subtitle: string;
  description: string;
  center_image: string | null;
  badge_text: string;
  order: number;
  is_active: boolean;
  features?: WhyChooseUsFeature[];
  benefits?: WhyChooseUsBenefit[];
  statistics?: WhyChooseUsStat[];
}

export interface SEOSettings {
  id: string;
  page_type: 'home' | 'services' | 'blog' | 'contact' | 'about';
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  robots_txt?: string;
  schema_markup?: any;
}

class ApiService {
  // Normalize list responses from the API. Some endpoints return a paginated
  // object with `results`, others return the array directly.
  private normalizeList(response: any) {
    const data = response?.data?.results ?? response?.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && !data.hasOwnProperty('status')) {
      return [data];
    }
    return [];
  }



  async getStatistics(): Promise<Statistics> {
    const stats: Statistics = {
      total_services: 0,
      total_products: 0,
      total_blog_posts: 0,
      pending_requests: 0,
      completed_requests: 0,
      total_testimonials: 0,
    };

    try {
      const fetchWithCatch = async (fetcher: () => Promise<any>, field?: string) => {
        try {
          return await fetcher();
        } catch (err) {
          console.warn(`Failed to fetch ${field || 'data'} for statistics:`, err);
          return [];
        }
      };

      const [servicesRes, productsRes, blogsRes, contactRequestsRes, serviceRequestsRes, testimonialsRes] = await Promise.all([
        fetchWithCatch(() => this.getServices(), 'services'),
        fetchWithCatch(() => this.getProducts(), 'products'),
        fetchWithCatch(() => this.getBlogPosts(), 'blogs'),
        fetchWithCatch(() => this.getContactRequests(), 'contact_requests'),
        fetchWithCatch(() => this.getServiceRequests(), 'service_requests'),
        fetchWithCatch(() => this.getTestimonials(), 'testimonials'),
      ]);

      const allRequests = [...(contactRequestsRes || []), ...(serviceRequestsRes || [])];

      const pending = allRequests.filter((r: any) => {
        const s = (r.status || '').toString().toLowerCase();
        return s === 'pending' || s === 'open' || s === 'new';
      }).length;

      const completed = allRequests.filter((r: any) => {
        const s = (r.status || '').toString().toLowerCase();
        return s === 'completed' || s === 'done' || s === 'closed';
      }).length;

      stats.total_services = (servicesRes || []).length;
      stats.total_products = (productsRes || []).length;
      stats.total_blog_posts = (blogsRes || []).length;
      stats.pending_requests = pending;
      stats.completed_requests = completed;
      stats.total_testimonials = (testimonialsRes || []).length;

      return stats;
    } catch (err) {
      console.error('Fatal error in getStatistics:', err);
      return stats;
    }
  }

  async getServices(): Promise<Service[]> {
    const response = await api.get('/api/v1/services/');
    return this.normalizeList(response);
  }

  async getContactRequests(): Promise<ContactRequest[]> {
    const response = await api.get('/api/v1/contact-requests/');
    return this.normalizeList(response);
  }

  async updateContactRequest(id: string, data: Partial<ContactRequest>): Promise<ContactRequest> {
    const response = await api.patch(`/api/v1/contact-requests/${id}/`, data);
    return response.data;
  }

  async getUsers(): Promise<UserProfile[]> {
    try {
      const response = await api.get('/api/v1/auth/users/');
      return this.normalizeList(response);
    } catch (err) {
      console.error('getUsers failed:', err);
      throw err;
    }
  }

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/api/v1/auth/users/${id}/delete/`);
  }

  async getUserDetail(id: string): Promise<User> {
    const response = await api.get(`/api/v1/users/${id}/`);
    return response.data;
  }

  /**
   * User modific methods for non-auth operations are no longer needed here.
   * User CRUD for admins is now available via createUser() in the auth section.
   * Old non-auth createUser/updateUser/deleteUser methods removed to avoid conflicts.
   */

  // Public site / admin endpoints mapped from OpenAPI
  async getAboutUs(is_active?: boolean) {
    const params: any = {};
    if (typeof is_active !== 'undefined') params.is_active = is_active;
    const response = await api.get('/api/v1/about-us/', { params });
    return this.normalizeList(response);
  }

  async createAboutUs(formData: FormData) {
    const response = await api.post('/api/v1/about-us/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateAboutUs(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/about-us/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteAboutUs(id: string) {
    const response = await api.delete(`/api/v1/about-us/${id}/`);
    return response.data;
  }

  async getBanners(params?: { banner_type?: string; is_active?: boolean }) {
    const response = await api.get('/api/v1/banners/', { params });
    return this.normalizeList(response);
  }

  async createBanner(formData: FormData) {
    const response = await api.post('/api/v1/banners/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateBanner(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/banners/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteBanner(id: string) {
    const response = await api.delete(`/api/v1/banners/${id}/`);
    return response.data;
  }

  async getServicesList(params?: { category?: string; is_active?: boolean; page?: number }) {
    const response = await api.get('/api/v1/services/', { params });
    return this.normalizeList(response);
  }

  async createService(formData: FormData) {
    const response = await api.post('/api/v1/services/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateService(slug: string, formData: FormData) {
    const response = await api.patch(`/api/v1/services/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteService(slug: string) {
    const response = await api.delete(`/api/v1/services/${slug}/`);
    return response.data;
  }

  // Service Categories
  async getServiceCategories(params?: { is_active?: boolean; search?: string }) {
    const response = await api.get('/api/v1/service-categories/', { params });
    return this.normalizeList(response);
  }

  async createServiceCategory(formData: FormData) {
    const response = await api.post('/api/v1/service-categories/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateServiceCategory(slug: string, formData: FormData) {
    const response = await api.patch(`/api/v1/service-categories/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteServiceCategory(slug: string) {
    const response = await api.delete(`/api/v1/service-categories/${slug}/`);
    return response.data;
  }

  async getServiceDetail(slug: string) {
    const response = await api.get(`/api/v1/services/${slug}/`);
    return response.data;
  }

  async getProducts(params?: { brand?: string; is_active?: boolean; search?: string }) {
    const response = await api.get('/api/v1/products/', { params });
    return this.normalizeList(response);
  }

  async getProductDetail(slug: string): Promise<Product> {
    const response = await api.get(`/api/v1/products/${slug}/`);
    return response.data;
  }

  async createProduct(formData: FormData): Promise<Product> {
    const response = await api.post('/api/v1/products/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateProduct(slug: string, formData: FormData): Promise<Product> {
    const response = await api.patch(`/api/v1/products/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteProduct(slug: string): Promise<void> {
    await api.delete(`/api/v1/products/${slug}/`);
  }

  async getBlogPosts(params?: { category?: string; page?: number; search?: string }) {
    const response = await api.get('/api/v1/blog-posts/', { params });
    return this.normalizeList(response);
  }

  async getBlogPostDetail(slug: string) {
    const response = await api.get(`/api/v1/blog-posts/${slug}/`);
    return response.data;
  }

  async createBlogPost(formData: FormData) {
    const response = await api.post('/api/v1/blog-posts/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateBlogPost(slug: string, formData: FormData) {
    const response = await api.patch(`/api/v1/blog-posts/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteBlogPost(slug: string) {
    const response = await api.delete(`/api/v1/blog-posts/${slug}/`);
    return response.data;
  }

  // Blog Categories
  async getBlogCategories(params?: { is_active?: boolean; search?: string }) {
    const response = await api.get('/api/v1/blog-categories/', { params });
    return this.normalizeList(response);
  }

  async createBlogCategory(formData: FormData) {
    const response = await api.post('/api/v1/blog-categories/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateBlogCategory(slug: string, formData: FormData) {
    const response = await api.patch(`/api/v1/blog-categories/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteBlogCategory(slug: string) {
    const response = await api.delete(`/api/v1/blog-categories/${slug}/`);
    return response.data;
  }

  async incrementBlogPostViews(slug: string) {
    const response = await api.post(`/api/v1/blog-posts/${slug}/increment_views/`);
    return response.data;
  }

  async publishBlogPost(slug: string) {
    const response = await api.post(`/api/v1/blog-posts/${slug}/publish/`);
    return response.data;
  }

  async unpublishBlogPost(slug: string) {
    const response = await api.post(`/api/v1/blog-posts/${slug}/unpublish/`);
    return response.data;
  }

  async getCompanyInfo() {
    const response = await api.get('/api/v1/company-info/');
    return response.data.results?.[0] || null;
  }

  async createCompanyInfo(formData: FormData) {
    const response = await api.post('/api/v1/company-info/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateCompanyInfo(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/company-info/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // SEO Settings
  async getSeoSettings(params?: { page_type?: string }) {
    const response = await api.get('/api/v1/seo-settings/', { params });
    return this.normalizeList(response);
  }

  async createSeoSettings(formData: FormData) {
    const response = await api.post('/api/v1/seo-settings/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getSeoSettingDetail(page_type: string) {
    const response = await api.get(`/api/v1/seo-settings/${page_type}/`);
    return response.data;
  }

  async updateSeoSetting(page_type: string, formData: FormData) {
    const response = await api.patch(`/api/v1/seo-settings/${page_type}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteSeoSetting(page_type: string) {
    const response = await api.delete(`/api/v1/seo-settings/${page_type}/`);
    return response.data;
  }

  async submitContactRequest(payload: Partial<ContactRequest>) {
    const response = await api.post('/api/v1/contact-requests/', payload);
    return response.data;
  }

  async submitServiceRequest(payload: any) {
    const response = await api.post('/api/v1/service-requests/', payload);
    return response.data;
  }

  // FAQs
  async getFaqs(params?: any) {
    const response = await api.get('/api/v1/faqs/', { params });
    return this.normalizeList(response);
  }

  async createFaq(payload: any) {
    const response = await api.post('/api/v1/faqs/', payload);
    return response.data;
  }

  async updateFaq(id: string, payload: any) {
    const response = await api.patch(`/api/v1/faqs/${id}/`, payload);
    return response.data;
  }

  async deleteFaq(id: string) {
    const response = await api.delete(`/api/v1/faqs/${id}/`);
    return response.data;
  }

  // Testimonials (support FormData uploads)
  async getTestimonials(params?: any) {
    const response = await api.get('/api/v1/testimonials/', { params });
    return this.normalizeList(response);
  }

  async createTestimonial(formData: FormData) {
    const response = await api.post('/api/v1/testimonials/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateTestimonial(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/testimonials/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteTestimonial(id: string) {
    const response = await api.delete(`/api/v1/testimonials/${id}/`);
    return response.data;
  }

  // Hero slides
  async getHeroSlides(params?: any) {
    const response = await api.get('/api/v1/hero-slides/', { params });
    return this.normalizeList(response);
  }

  async createHeroSlide(formData: FormData) {
    try {
      const response = await api.post('/api/v1/hero-slides/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err: any) {
      console.error('HeroSlide create failed:', err.response?.data);
      throw err;
    }
  }

  async updateHeroSlide(id: string, formData: FormData) {
    try {
      const response = await api.patch(`/api/v1/hero-slides/${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err: any) {
      console.error('HeroSlide update failed:', err.response?.data);
      throw err;
    }
  }

  async deleteHeroSlide(id: string) {
    const response = await api.delete(`/api/v1/hero-slides/${id}/`);
    return response.data;
  }

  // Gallery
  async getGallery(params?: any) {
    const response = await api.get('/api/v1/gallery/', { params });
    return this.normalizeList(response);
  }

  async createGalleryItem(formData: FormData) {
    const response = await api.post('/api/v1/gallery/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateGalleryItem(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/gallery/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteGalleryItem(id: string) {
    const response = await api.delete(`/api/v1/gallery/${id}/`);
    return response.data;
  }

  // Service requests
  async getServiceRequests(params?: any) {
    const response = await api.get('/api/v1/service-requests/', { params });
    return this.normalizeList(response);
  }

  async updateServiceRequest(id: string, payload: any) {
    const response = await api.patch(`/api/v1/service-requests/${id}/`, payload);
    return response.data;
  }

  // Why Choose Us
  async getWhyChooseUs(params?: any) {
    const response = await api.get('/api/v1/why-choose-us/', { params });
    return this.normalizeList(response);
  }

  async createWhyChooseUs(formData: FormData) {
    const response = await api.post('/api/v1/why-choose-us/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateWhyChooseUs(id: string, formData: FormData) {
    const response = await api.patch(`/api/v1/why-choose-us/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteWhyChooseUs(id: string) {
    const response = await api.delete(`/api/v1/why-choose-us/${id}/`);
    return response.data;
  }

  // Why Choose Us Benefits
  async getWhyChooseUsBenefits(params?: any) {
    const response = await api.get('/api/v1/why-choose-us-benefits/', { params });
    return this.normalizeList(response);
  }

  async createWhyChooseUsBenefit(payload: Partial<WhyChooseUsBenefit>) {
    const response = await api.post('/api/v1/why-choose-us-benefits/', payload);
    return response.data;
  }

  async updateWhyChooseUsBenefit(id: string, payload: Partial<WhyChooseUsBenefit>) {
    const response = await api.patch(`/api/v1/why-choose-us-benefits/${id}/`, payload);
    return response.data;
  }

  async deleteWhyChooseUsBenefit(id: string) {
    const response = await api.delete(`/api/v1/why-choose-us-benefits/${id}/`);
    return response.data;
  }

  // Why Choose Us Features
  async getWhyChooseUsFeatures(params?: any) {
    const response = await api.get('/api/v1/why-choose-us-features/', { params });
    return this.normalizeList(response);
  }

  async createWhyChooseUsFeature(payload: Partial<WhyChooseUsFeature>) {
    const response = await api.post('/api/v1/why-choose-us-features/', payload);
    return response.data;
  }

  async updateWhyChooseUsFeature(id: string, payload: Partial<WhyChooseUsFeature>) {
    const response = await api.patch(`/api/v1/why-choose-us-features/${id}/`, payload);
    return response.data;
  }

  async deleteWhyChooseUsFeature(id: string) {
    const response = await api.delete(`/api/v1/why-choose-us-features/${id}/`);
    return response.data;
  }

  // Why Choose Us Stats
  async getWhyChooseUsStats(params?: any) {
    const response = await api.get('/api/v1/why-choose-us-stats/', { params });
    return this.normalizeList(response);
  }

  async createWhyChooseUsStat(payload: Partial<WhyChooseUsStat>) {
    const response = await api.post('/api/v1/why-choose-us-stats/', payload);
    return response.data;
  }

  async updateWhyChooseUsStat(id: string, payload: Partial<WhyChooseUsStat>) {
    const response = await api.patch(`/api/v1/why-choose-us-stats/${id}/`, payload);
    return response.data;
  }

  async deleteWhyChooseUsStat(id: string) {
    const response = await api.delete(`/api/v1/why-choose-us-stats/${id}/`);
    return response.data;
  }

  // ========== Authentication & User Management ==========

  // Login
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/api/v1/auth/token/', credentials);
    return response.data;
  }

  // Token Refresh
  async refreshToken(refreshToken: string): Promise<TokenRefreshResponse> {
    const response = await api.post('/api/v1/auth/token/refresh/', { refresh: refreshToken });
    return response.data;
  }

  // Token Verify
  async verifyToken(token: string): Promise<void> {
    await api.post('/api/v1/auth/token/verify/', { token });
  }

  // Get Current User Profile
  async getCurrentUserProfile(): Promise<UserProfile> {
    const response = await api.get('/api/v1/auth/profile/');
    return response.data;
  }

  // Update Current User Profile
  async updateCurrentUserProfile(data: UserProfileUpdateRequest): Promise<UserProfile> {
    const formData = new FormData();
    if (data.email) formData.append('email', data.email);
    if (data.first_name) formData.append('first_name', data.first_name);
    if (data.last_name) formData.append('last_name', data.last_name);
    if (data.phone) formData.append('phone', data.phone);
    if (data.profile_image) formData.append('profile_image', data.profile_image);

    const response = await api.put('/api/v1/auth/profile/update/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async patchCurrentUserProfile(data: Partial<UserProfileUpdateRequest>): Promise<UserProfile> {
    const formData = new FormData();
    if (data.email) formData.append('email', data.email);
    if (data.first_name) formData.append('first_name', data.first_name);
    if (data.last_name) formData.append('last_name', data.last_name);
    if (data.phone) formData.append('phone', data.phone);
    if (data.profile_image) formData.append('profile_image', data.profile_image);

    const response = await api.patch('/api/v1/auth/profile/update/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Change Password (current user)
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await api.post('/api/v1/auth/change-password/', data);
  }

  // Reset Password (admin only)
  async resetUserPassword(data: ResetPasswordRequest): Promise<void> {
    await api.post('/api/v1/auth/reset-password/', data);
  }

  // Create User (admin only)
  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await api.post('/api/v1/auth/create-user/', data);
    return response.data;
  }
}

export const apiService = new ApiService();